import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiEdit, FiArrowLeft, FiCheck, FiX, FiSend, FiMoreHorizontal } from 'react-icons/fi';
import visitService from '../../services/visitService';
import { VISIT_STATUS, VISIT_TYPES } from '../../utils/constants';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { common as commonMessages } from '../../utils/validationMessages';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { Modal } from '../Common/Modal';
import './VisitDetail.css';
import './VisitActionsModal.css';

const VisitDetail = () => {
  const { id } = useParams();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [toast, setToast] = useState(null);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);

  useEffect(() => {
    fetchVisit();
  }, [id]);

  const fetchVisit = async () => {
    try {
      setLoading(true);
      const response = await visitService.getById(id);
      setVisit(response);
      setError(null);
    } catch (err) {
      setError('Failed to load visit details');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await visitService.confirm(id);
      fetchVisit();
    } catch (err) {
      setError('Failed to confirm visit');
    }
  };

  const handleCancel = async () => {
    try {
      await visitService.cancel(id, { reason: cancelReason });
      setShowCancelModal(false);
      fetchVisit();
    } catch (err) {
      setError('Failed to cancel visit');
    }
  };

  const handleSendConfirmation = async () => {
    try {
      await visitService.sendConfirmationLetter(id);
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      setError('Failed to send confirmation letter');
    }
  };

  if (loading) return <Spinner />;
  if (error) return <Alert type="error" message={error} />;
  if (!visit) return <p>Visit not found.</p>;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case VISIT_STATUS.COMPLETED:
        return 'badge-success';
      case VISIT_STATUS.ONGOING:
        return 'badge-info';
      case VISIT_STATUS.CONFIRMED:
        return 'badge-warning';
      case VISIT_STATUS.CANCELLED:
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  return (
    <div className="visit-detail">
      <div className="page-header">
        <Link to="/visits" className="back-link">
          <FiArrowLeft /> Back to Visit List
        </Link>
        <h2>Visit Details</h2>
        <div className="header-actions">
          <Link to={`/visits/${id}/edit`} className="btn btn-secondary">
            <FiEdit /> Edit
          </Link>
        </div>
      </div>

      <div className="detail-card">
        <div className="card-header">
          <h3>{visit.siteName}</h3>
          <span className={`badge ${getStatusBadgeClass(visit.visitStatus)}`}>
            {visit.visitStatus}
          </span>
        </div>

        <div className="card-body">
          <div className="info-grid">
            <div className="info-item">
              <strong>Visit Type:</strong>
              <p>{VISIT_TYPES[visit.visitType]}</p>
            </div>
            <div className="info-item">
              <strong>Scheduled Date:</strong>
              <p>
                {formatDateDDMMMYYYY(visit.plannedStartDate)} -{' '}
                {formatDateDDMMMYYYY(visit.plannedEndDate)}
              </p>
            </div>
            <div className="info-item">
              <strong>CRA:</strong>
              <p>{visit.craName}</p>
            </div>
            <div className="info-item">
              <strong>Protocol Window:</strong>
              <p>
                {formatDateDDMMMYYYY(visit.protocolWindowStart)} -{' '}
                {formatDateDDMMMYYYY(visit.protocolWindowEnd)}
              </p>
            </div>
          </div>

          <div className="visit-actions">
            {visit.visitStatus === VISIT_STATUS.DRAFT && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setIsActionsModalOpen(true)}
              >
                <FiMoreHorizontal /> Actions
              </button>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isActionsModalOpen}
        onClose={() => setIsActionsModalOpen(false)}
        title="Visit Actions"
        size="medium"
        className="visit-actions-modal"
      >
        <div className="visit-actions-header">
          <div className="visit-actions-subtitle">
            {visit?.siteName} · {VISIT_TYPES[visit?.visitType] || visit?.visitType}
          </div>
        
        </div>
        <div className="visit-actions-buttons">
          <button
            className="btn btn-success btn-block"
            onClick={() => { setIsActionsModalOpen(false); handleConfirm(); }}
          >
            <FiCheck /> Confirm Visit
          </button>
          <button
            className="btn btn-error btn-block"
            onClick={() => {
              setIsActionsModalOpen(false);
              setShowCancelModal(true);
            }}
          >
            <FiX /> Cancel Visit
          </button>
          <button
            className="btn btn-primary btn-block"
            onClick={() => { setIsActionsModalOpen(false); handleSendConfirmation(); }}
          >
            <FiSend /> Send Confirmation Letter
          </button>
        </div>
      </Modal>

      {showCancelModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Cancel Visit</h3>
            <p>Please provide a reason for cancellation:</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows="3"
            />
            <div className="modal-actions">
              <button className="btn btn-error" onClick={handleCancel}>
                Confirm Cancellation
              </button>
              <button className="btn btn-outline" onClick={() => setShowCancelModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </div>
  );
};

export default VisitDetail;
